/**
 * What each GM is likely to keep, and why.
 *
 * TWO KINDS OF ROW, NEVER MIXED SILENTLY. A submitted declaration is a FACT — the man has told
 * the league what he is keeping, and there is nothing to project. Everything else is a GUESS off
 * the ADP board, and the difference is carried on every row as `basis` so no screen can print one
 * as though it were the other. That distinction is the whole reason this module exists rather
 * than a sort in a component.
 *
 * THE GUESS IS VALUE AGAINST COST, and both halves come from somewhere real:
 *
 *   COST is the Grid, already computed per player by the keeper build (2019-II-1). It is what the
 *   rules charge, not an opinion.
 *
 *   VALUE is where the weighted ADP board says he goes — weighted towards sources that answer
 *   this league's own rules, ten teams and half PPR (2016-IV-1). See `sourceWeights`.
 *
 * The edge is `cost − value` in rounds. A player worth a 3rd who costs an 11th is +8 and is kept
 * by anybody; a player worth an 11th who costs a 3rd is −8 and is kept by nobody. Sorting on that
 * and cutting at the cap reproduces what managers actually do, which is the only test that
 * matters — a projection that disagrees with every slate it can be checked against is wrong
 * however principled its reasoning.
 *
 * WHAT IT DELIBERATELY WILL NOT DO. It does not project the franchise tag. The tag costs three
 * keeper slots for one player (2025 P05 §2) and is used at most once a year by one manager on a
 * hunch; a model that guessed at it would be wrong nine times out of ten and would distort the
 * slate it was attached to. Tagged players in a SUBMITTED slate are honoured, because that is a
 * fact. Nobody's tag is ever predicted.
 */

import type { LeagueConfig } from './config.js';
import { assignKeeperPicks } from './draftOrder.js';
import { weightedConsensus, type Consensus } from './draftTools.js';
import { lookupByName, looseName, nameKeys, normName } from './playerNames.js';

/** A keeper candidate as the keeper build already describes him. */
export interface Candidate {
  player: string;
  /** Uppercase GM id who holds him. */
  gm: string;
  pos?: string;
  nflTeam?: string | null;
  /** What the Grid charges to keep him, or null when he cannot be kept at all. */
  cost: number | null;
  /** Farm graduate — his rookie year is mandatory (2013-III-1 §5). */
  mandatory?: boolean;
  farm?: boolean;
}

export type Basis =
  /** He is on a submitted declaration. Not a projection at all. */
  | 'declared'
  /** The rules make him mandatory, whoever the manager is. */
  | 'mandatory'
  /** The ADP board says he is worth more than he costs. */
  | 'projected'
  /**
   * He could be kept and is not being kept.
   *
   * Not a projection — the model has already decided against him, or his manager has. He is here
   * because the columns beside him (what he costs, what he is worth, the gap) are the reason for
   * that decision, and a screen that only lists the eight a manager chose cannot show him why he
   * was right to pass on the ninth.
   */
  | 'eligible';

export interface ProjectedKeeper {
  player: string;
  gm: string;
  pos?: string;
  nflTeam?: string | null;
  basis: Basis;
  /** What the Grid charges. */
  cost: number;
  /**
   * The round the weighted board puts him in, or null when no source ranks him.
   *
   * CLAMPED TO ONE PAST THE LAST ROUND. The ADP file is a thousand players deep and this league
   * drafts sixteen rounds of ten, so a raw index put a fringe player at "Round 37" — a round that
   * does not exist, printed on a screen next to rounds that do. Anything past the board is worth
   * the same thing (nothing) and says so through `beyondBoard`.
   */
  valueRound: number | null;
  /** True when the board does not reach him at all — he would go undrafted here. */
  beyondBoard?: boolean;
  /**
   * The same question asked of the RAW board, with nobody removed.
   *
   * WHY BOTH NUMBERS ARE WORTH SHOWING. `valueRound` is already adjusted — it is read off a board
   * with every projected and declared keeper taken out of it, which is the board a manager will
   * actually be drafting from. That correction is large and invisible: a player the raw ADP puts
   * in Round 6 is often a Round 2 pick once forty keepers come off the top, and the edge that
   * decides whether he is worth the tax is computed from the adjusted figure.
   *
   * Printing only one of them left the screen saying "worth R6" while pricing him as an R2, and
   * left nobody able to check the correction. So: `adpRound` is what the market says, `valueRound`
   * is what this league's board says, and the gap between them is the whole model.
   */
  adpRound: number | null;
  adpBeyondBoard?: boolean;
  /** cost − valueRound, from the ADJUSTED round. Positive is a bargain. Null when unranked. */
  edge: number | null;
  /** The pick this would actually consume once collisions are resolved (2015-III-7). */
  round: number | null;
  franchiseTagged?: boolean;
  /** One line for the screen. */
  why: string;
}

export interface TeamProjection {
  gm: string;
  /** True when this team's row came from a submitted slate rather than the model. */
  declared: boolean;
  keepers: ProjectedKeeper[];
  /** Slots consumed, counting a franchise tag as three (2025 P05 §2). */
  slotsUsed: number;
  /** Anything that could not be resolved — a keeper with no pick left to pay with. */
  errors: string[];
  /**
   * Every other keepable player on this roster, priced the same way and sorted by edge.
   *
   * Always computed, never mixed into `keepers`. A screen showing them has to say so — they are
   * the road not taken, and printing them alongside a declaration as though they were on it would
   * be the same class of lie `basis` exists to prevent.
   */
  others: ProjectedKeeper[];
}

export interface ProjectInput {
  candidates: readonly Candidate[];
  /** GM -> the rounds he owns, after trades. */
  ownedRounds: Readonly<Record<string, readonly number[]>>;
  /** GM -> his submitted slate. Present teams are reported as fact and never modelled. */
  submitted?: Readonly<Record<string, ReadonlyArray<{ player: string; franchiseTagged?: boolean; elevatedTo?: number }>>>;
  config: LeagueConfig;
  /** Injectable so a test does not depend on the bundled ADP file. */
  board?: readonly Consensus[];
  /**
   * How many keepers the model gives a team that has not declared.
   *
   * Defaults to the FREE allowance rather than the maximum, and that is a judgement worth stating:
   * slots beyond the free ones cost real FAAB in luxury tax (2023-keeper-expansion-eight), so most managers stop at
   * the free four unless a player is a screaming bargain. The model takes a taxed slot only when
   * the edge is big enough to be obviously worth paying for — see `TAXED_SLOT_EDGE`.
   */
  freeOnly?: boolean;
}

/**
 * How good a bargain has to be before the model believes somebody will pay tax for him.
 *
 * CALIBRATED AGAINST THE LEAGUE'S OWN SLATES, not chosen for tidiness. Every slot past the fourth
 * costs a flat ₣22 marginal (2023-keeper-expansion-eight gives cumulative 22/44/66/88), so the bar is the same
 * for the fifth keeper as the eighth, and the only question is how many rounds of value ₣22 buys.
 *
 * Sweeping the threshold against the 2026 candidate pool gives these mean slate sizes:
 *
 *     bar   1     2     3     4     5
 *     mean  6.10  4.90  4.50  4.10  4.00
 *
 * The league actually declared 4.60 keepers in 2025, 5.80 in 2024 and 5.40 in 2023 — a
 * three-year mean of 5.27. Two rounds is the closest fit and sits inside the observed range;
 * four, the first value tried, produced a flat 4.10 and would have told Tim that nobody pays
 * tax in a league where everybody does.
 *
 * KNOWN LIMITATION. The model's floor is the free allowance, but real slates have gone as low as
 * two. It has no way to produce that: a manager keeping fewer than four is usually rebuilding, or
 * has traded the picks the keepers would cost, and neither is visible in a value-against-cost
 * comparison. The projection is a floor on those teams, not a prediction.
 */
export const TAXED_SLOT_EDGE = 2;

/**
 * Where the weighted board puts a player, in OUR rounds.
 *
 * KNOWN-GONE PLAYERS ARE REMOVED FIRST and the rest re-ranked, the same correction `adjusted()`
 * makes: a receiver ranked 60th with thirty players above him already kept is really the 30th
 * available here. "Known gone" is exactly the set this module is certain about — mandatory farm
 * graduates and anybody on a submitted slate — and deliberately not the players it is in the
 * middle of guessing at, which would be circular.
 *
 * The residual bias is worth naming: the teams still being modelled will also keep players, so
 * these value rounds remain slightly optimistic. It falls on every candidate about equally, so
 * the ORDER — which is what the model sorts on — is unaffected; only the absolute edge is, and
 * that is why the cut-offs below are round numbers rather than tuned constants.
 */
function valueRounds(
  board: readonly Consensus[], teams: number, gone: ReadonlySet<string>,
): Map<string, number> {
  const out = new Map<string, number>();
  const loose: Array<[string, number]> = [];
  let i = 0;
  for (const c of board) {
    const key = normName(c.name);
    if (out.has(key)) continue;
    /*
     * EVERY player gets a value round, including the ones already off the board — the counter is
     * what skips them, not the assignment. The question "what is he worth" is answered by where
     * he would go if HE were available, in a pool that excludes the OTHER keepers, and that is
     * exactly the round his position lands in once the players ahead of him are removed.
     *
     * Dropping gone players out of the map instead was a real bug and a badly-behaved one: the
     * second pass removed everybody the first pass had projected, they came back as UNRANKED
     * rather than as bargains, and every slate shrank. Silent, plausible, and wrong.
     */
    const round = Math.floor(i / teams) + 1;
    out.set(key, round);
    loose.push([looseName(c.name), round]);
    if (!gone.has(key)) i += 1;
  }
  /* Suffix-stripped keys fill in only where nothing literal claimed them — see `indexByName`. */
  for (const [k, round] of loose) if (k && !out.has(k)) out.set(k, round);
  return out;
}


/**
 * TWO PASSES, because the value of a keeper depends on who else is kept.
 *
 * A single pass has a bias that is not harmless. Value is read off a board that still contains
 * forty-odd players who never reach the draft, so every candidate looks like he goes earlier than
 * he really would here, so every edge comes out several rounds too small — and the model then
 * refuses to pay tax for anybody. Checked against the league's own history that is plainly wrong:
 * real slates run 4.6 to 5.8 keepers and the model produced a flat 4.
 *
 * The fix is the correction `adjusted()` already makes, applied twice: project once to find out
 * who is likely gone, take them off the board, re-rank, and project again against the board a
 * manager would actually be drafting from. A third pass moves almost nothing — the set is stable
 * once the obvious keeps are removed — so it stops at two rather than iterating to a fixed point
 * nobody could explain.
 */
export function projectKeepers(input: ProjectInput): TeamProjection[] {
  const first = runPass(input, new Set());
  const likely = new Set<string>();
  for (const t of first) for (const k of t.keepers) for (const n of nameKeys(k.player)) likely.add(n);
  return runPass(input, likely);
}

function runPass(input: ProjectInput, alsoGone: ReadonlySet<string>): TeamProjection[] {
  const { candidates, ownedRounds, config } = input;
  const submitted = input.submitted ?? {};
  const board = input.board ?? weightedConsensus();

  /* Certainly off the board: the mandatory graduates and every declared keeper. */
  const gone = new Set<string>(alsoGone);
  /* Every spelling, not just the canonical one — see `nameKeys`. A graduate missed here would be
     left in the ranking and would push everybody behind him a round too late. */
  for (const c of candidates) {
    if (c.mandatory && c.cost !== null) for (const k of nameKeys(c.player)) gone.add(k);
  }
  for (const slate of Object.values(submitted)) {
    for (const k of slate) for (const n of nameKeys(k.player)) gone.add(n);
  }
  const value = valueRounds(board, config.teams, gone);
  /* The uncorrected board: what the market says before any keeper comes off it. Carried alongside
     so the screen can show the correction rather than only its result. */
  const rawValue = valueRounds(board, config.teams, new Set());

  const byGm = new Map<string, Candidate[]>();
  for (const c of candidates) {
    const gm = c.gm.toUpperCase();
    if (!byGm.has(gm)) byGm.set(gm, []);
    byGm.get(gm)!.push(c);
  }
  for (const gm of Object.keys(ownedRounds)) {
    if (!byGm.has(gm.toUpperCase())) byGm.set(gm.toUpperCase(), []);
  }

  const out: TeamProjection[] = [];
  for (const [gm, pool] of [...byGm].sort(([a], [b]) => a.localeCompare(b))) {
    const slate = submitted[gm];
    const rows = slate
      ? fromDeclaration(gm, slate, pool, value, rawValue, config.draftRounds)
      : model(gm, pool, value, rawValue, config, input.freeOnly !== false);

    /*
     * The pick each keeper actually consumes, through the SAME resolver the declaration screen
     * and the live board use. A projection that placed keepers by its own rules would disagree
     * with the board it is meant to be predicting.
     */
    const owned = ownedRounds[gm] ?? ownedRounds[gm.toUpperCase()] ?? [];
    const { assigned, errors } = assignKeeperPicks(
      rows.map((r) => ({
        id: r.player,
        costRound: r.cost,
        preferred: slate?.find((k) => k.player === r.player)?.elevatedTo ?? null,
      })),
      owned,
    );
    for (const r of rows) r.round = assigned.get(r.player) ?? null;

    /*
     * THE ROAD NOT TAKEN. Everybody keepable on this roster who is not on the slate above, priced
     * identically and sorted best-first. `round` stays null on these: they consume no pick, and
     * printing a cost round in the PICK column would read as an allocation that has not happened.
     */
    const chosen = new Set(rows.flatMap((r) => nameKeys(r.player)));
    const others = pool
      .filter((c) => c.cost !== null && !nameKeys(c.player).some((n) => chosen.has(n)))
      .map((c) => priceRow(gm, c, value, rawValue, config.draftRounds, 'eligible', ''))
      .sort((a, b) => (b.edge ?? -99) - (a.edge ?? -99));

    const tagged = rows.filter((r) => r.franchiseTagged).length;
    out.push({
      gm,
      declared: Boolean(slate),
      keepers: rows,
      slotsUsed: rows.length + tagged * 2,
      errors,
      others,
    });
  }
  return out;
}

/**
 * One row, priced.
 *
 * Every list on this screen — the declaration, the model, the road not taken — has to be priced
 * the same way or the columns cannot be compared down the page. This was written out three times
 * before it was written once.
 */
function priceRow(
  gm: string,
  c: Candidate,
  value: Map<string, number>,
  rawValue: Map<string, number>,
  draftRounds: number,
  basis: Basis,
  why: string,
): ProjectedKeeper {
  const raw = lookupByName(value, c.player) ?? null;
  const past = raw !== null && raw > draftRounds;
  const v = raw === null ? null : Math.min(raw, draftRounds + 1);
  const adpRaw = lookupByName(rawValue, c.player) ?? null;
  const adpPast = adpRaw !== null && adpRaw > draftRounds;
  const adp = adpRaw === null ? null : Math.min(adpRaw, draftRounds + 1);
  return {
    player: c.player,
    gm,
    pos: c.pos,
    nflTeam: c.nflTeam,
    basis,
    cost: c.cost!,
    valueRound: v,
    beyondBoard: past,
    adpRound: adp,
    adpBeyondBoard: adpPast,
    edge: v === null ? null : c.cost! - v,
    round: null,
    why,
  };
}

/** A submitted slate, reported rather than modelled. */
function fromDeclaration(
  gm: string,
  slate: ReadonlyArray<{ player: string; franchiseTagged?: boolean }>,
  pool: readonly Candidate[],
  value: Map<string, number>,
  rawValue: Map<string, number>,
  DRAFT_ROUNDS_HINT: number,
): ProjectedKeeper[] {
  const out: ProjectedKeeper[] = [];
  for (const k of slate) {
    const keys = new Set(nameKeys(k.player));
    const c = pool.find((x) => nameKeys(x.player).some((n) => keys.has(n)));
    if (!c || c.cost === null) continue;
    out.push({
      ...priceRow(gm, c, value, rawValue, DRAFT_ROUNDS_HINT, 'declared',
        'Declared — he has submitted this slate.'),
      franchiseTagged: k.franchiseTagged,
    });
  }
  return out;
}

/** Everybody else: mandatory first, then the best bargains until the allowance runs out. */
function model(
  gm: string,
  pool: readonly Candidate[],
  value: Map<string, number>,
  rawValue: Map<string, number>,
  config: LeagueConfig,
  freeOnly: boolean,
): ProjectedKeeper[] {
  const keepable = pool.filter((c) => c.cost !== null);
  const row = (c: Candidate, basis: Basis, why: string): ProjectedKeeper =>
    priceRow(gm, c, value, rawValue, config.draftRounds, basis, why);

  // 2013-III-1 §5: a farm graduate's rookie year is not a choice, so he is in before anything
  // is weighed. He also consumes one of the free slots, which is what makes him expensive.
  const forced = keepable.filter((c) => c.mandatory)
    .map((c) => {
      const r = row(c, 'mandatory', 'Farm graduate — his rookie year is mandatory (2013-III-1 §5).');
      /*
       * A graduate nobody would draft is the 2019-II-5 case in the making — the rule that costs a
       * GM the slot when the NFL cuts him. Worth saying here rather than leaving him as a row
       * with a large negative number and no explanation.
       */
      if (r.beyondBoard || r.edge === null) {
        r.why += ' No board reaches him, so the slot is the cost of the speculation.';
      }
      return r;
    });

  const rest = keepable.filter((c) => !c.mandatory)
    .map((c) => row(c, 'projected', ''))
    /*
     * An unranked player sorts last rather than being dropped. No source ranking him is weak
     * evidence that nobody wants him, but it is not the same as a bad edge — he may simply be
     * outside a 219-player file — so he stays visible and is only taken if slots go begging.
     */
    .sort((a, b) => (b.edge ?? -99) - (a.edge ?? -99));

  const out = [...forced];
  for (const r of rest) {
    /* The hard cap. 2023-keeper-expansion-eight sets the maximum and nothing buys a ninth slot. */
    if (out.length >= config.maxKeepers) break;

    /*
     * NOBODY KEEPS A PLAYER WHO COSTS MORE THAN HE IS WORTH. This is the one line the model will
     * not cross to fill a slot: an empty keeper slot is free and a bad keeper is not, so a
     * negative edge ends the list rather than padding it. `rest` is sorted by edge, so the first
     * negative one means every candidate behind it is negative too.
     */
    if (r.edge !== null && r.edge < 0) break;

    /*
     * Past the free allowance every further slot costs FAAB (2023-keeper-expansion-eight), so the bar rises. In
     * the default mode the model only pays tax for a bargain big enough to obviously cover it;
     * an unranked player never clears that bar, because there is no evidence he is a bargain at
     * all. `freeOnly: false` says "show me everything he could legally keep" and drops the bar
     * to the same non-negative test as the free slots.
     */
    const beyondFree = out.length >= config.freeKeepers;
    if (beyondFree && freeOnly && (r.edge ?? -99) < TAXED_SLOT_EDGE) break;
    if (beyondFree && !freeOnly && r.edge === null) break;

    r.why = r.edge === null
      ? 'No loaded source ranks him, so there is nothing to weigh his cost against.'
      : r.edge > 0
        ? `Worth a Round ${r.valueRound} pick and costs Round ${r.cost} — ${r.edge} rounds of value.`
        : `Costs about what he is worth — Round ${r.cost} either way.`;
    if (beyondFree) r.why += ' Taxed slot, taken because the bargain covers the tax (2023-keeper-expansion-eight).';
    out.push(r);
  }
  return out;
}
